/** Fuel price helpers for invoice creation (base price + per-gallon margin). */

export function parseFuelMargin(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function effectiveFuelPricePerGallon(basePrice, margin) {
  return (parseFloat(basePrice) || 0) + parseFuelMargin(margin);
}

export function getFuelMarginsFromForm(formData) {
  return {
    diesel: parseFuelMargin(formData?.diesel_fuel_margin),
    def: parseFuelMargin(formData?.def_fuel_margin),
    dyed: parseFuelMargin(formData?.dyed_fuel_margin),
  };
}

/** YYYY-MM-DD key for pricing lookup (scheduled delivery date preferred). */
export function getJobPricingDateKey(job) {
  if (!job) return null;
  const raw = job.scheduled_date || job.completed_at;
  if (!raw) return null;
  const isoPrefix = String(raw).match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoPrefix) return isoPrefix[1];
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Return pricing row with margins added to each fuel unit price. */
export function applyFuelMarginsToPricing(pricing, margins) {
  if (!pricing) return null;
  const diesel = effectiveFuelPricePerGallon(pricing.diesel_price_per_liter, margins.diesel);
  const defPrice = effectiveFuelPricePerGallon(pricing.def_price_per_liter, margins.def);
  const dyed = effectiveFuelPricePerGallon(pricing.dyed_price_per_liter, margins.dyed);
  return {
    ...pricing,
    diesel_price_per_liter: diesel,
    def_price_per_liter: defPrice,
    dyed_price_per_liter: dyed,
    diesel_price_per_gallon: diesel,
    def_price_per_gallon: defPrice,
    dyed_price_per_gallon: dyed,
  };
}

export function fuelCostFromDeliveredQuantities(
  { dieselQty, defQty, dyedQty },
  billedPricing
) {
  if (!billedPricing) return 0;
  let total = 0;
  if (dieselQty > 0) {
    total += dieselQty * parseFloat(billedPricing.diesel_price_per_liter);
  }
  if (defQty > 0) {
    total += defQty * parseFloat(billedPricing.def_price_per_liter);
  }
  if (dyedQty > 0) {
    total += dyedQty * parseFloat(billedPricing.dyed_price_per_liter);
  }
  return total;
}

/** Round to cents (matches backend ROUND_HALF_UP for typical amounts). */
export function roundMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Invoice total = (delivery + fuel) + tax + credit card surcharge.
 * Surcharge is a percent of (subtotal + tax), rounded to cents.
 */
export function computeInvoiceTotals({
  deliveryFees = 0,
  fuelCost = 0,
  tax = 0,
  surchargePercent = 3,
} = {}) {
  const subtotal = (parseFloat(deliveryFees) || 0) + (parseFloat(fuelCost) || 0);
  const taxAmt = parseFloat(tax) || 0;
  const baseBeforeSurcharge = subtotal + taxAmt;
  const pctRaw = parseFloat(surchargePercent);
  const pct = Number.isFinite(pctRaw) && pctRaw >= 0 ? pctRaw : 3;
  const surcharge = roundMoney(baseBeforeSurcharge * (pct / 100));
  const total = roundMoney(baseBeforeSurcharge + surcharge);
  return {
    subtotal,
    tax: taxAmt,
    baseBeforeSurcharge,
    surchargePercent: pct,
    surcharge,
    total,
  };
}
